import React, { useState, useEffect } from 'react'
import { useCompatRouter } from '../hooks/useCompatRouter'
import { fetchPublicRoomsFromSupabase } from '../services/publicSupabase'

interface RoomType {
  id: number
  name: string
  type: string
  roomNumber: string
  capacity: number
  description: string
  amenities: string[]
  images: string[]
  hero_image_index: number
  pricing: {
    high_season: number
    mid_season: number
    low_season: number
    admin_fee: number
    breakage_deposit: number
    min_stay: number
  }
  price: number
  color: string
  available: boolean
  available_rooms: number
}

interface ExpandedImageState {
  roomId: number
  roomName: string
  roomType: string
  imageUrl: string
  imageIndex: number
  imageCount: number
}

const colorMap: Record<string, string> = {
  blue: 'from-blue-400 to-blue-600',
  purple: 'from-purple-400 to-purple-600',
  orange: 'from-orange-400 to-orange-600',
  green: 'from-green-400 to-green-600',
  red: 'from-red-400 to-red-600',
}

const canonicalizeUnitType = (value: string): string | null => {
  const normalized = String(value || '').trim().toLowerCase()

  if (!normalized) {
    return null
  }

  if (normalized.includes('bigger')) {
    return 'Bigger 6-Sleeper Unit'
  }

  if (normalized.includes('4')) {
    return '4-Sleeper Unit'
  }

  if (normalized.includes('6')) {
    return '6-Sleeper Unit'
  }

  return value
}

const ROOM_TYPE_TABS = [
  { value: 'All', label: 'All' },
  { value: '4-Sleeper Unit', label: '4-Sleeper Units' },
  { value: '6-Sleeper Unit', label: '6-Sleeper Units' },
  { value: 'Bigger 6-Sleeper Unit', label: 'Bigger 6-Sleeper Units' },
] as const

const RoomsPage: React.FC = () => {
  const router = useCompatRouter()
  const [rooms, setRooms] = useState<RoomType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedType, setSelectedType] = useState('All')
  const [activeImageIndexes, setActiveImageIndexes] = useState<Record<number, number>>({})
  const [expandedImage, setExpandedImage] = useState<ExpandedImageState | null>(null)

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    const requestedType = Array.isArray(router.query.type) ? router.query.type[0] : router.query.type

    const normalizedType = canonicalizeUnitType(String(requestedType || ''))
    setSelectedType(normalizedType || 'All')
  }, [router.isReady, router.query.type])

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const data = await fetchPublicRoomsFromSupabase()
        setRooms(data)
      } catch {
        setError('Unable to load units right now.')
      } finally {
        setLoading(false)
      }
    }
    void fetchRooms()
  }, [])

  const unitTypes = ROOM_TYPE_TABS

  const filteredRooms = selectedType === 'All'
    ? rooms
    : rooms.filter((room) => (canonicalizeUnitType(room.type) || room.type) === selectedType)

  const getActiveImageIndex = (room: RoomType) => {
    if (room.images.length === 0) {
      return 0
    }

    const preferredIndex = activeImageIndexes[room.id] ?? room.hero_image_index ?? 0
    return Math.min(Math.max(preferredIndex, 0), room.images.length - 1)
  }

  const setRoomImageIndex = (room: RoomType, nextIndex: number) => {
    if (room.images.length === 0) {
      return
    }

    const safeIndex = Math.min(Math.max(nextIndex, 0), room.images.length - 1)
    setActiveImageIndexes(current => ({
      ...current,
      [room.id]: safeIndex,
    }))
  }

  const openExpandedImage = (room: RoomType, imageUrl: string, imageIndex: number) => {
    setExpandedImage({
      roomId: room.id,
      roomName: room.name,
      roomType: room.type,
      imageUrl,
      imageIndex,
      imageCount: room.images.length,
    })
  }

  const stepExpandedImage = (direction: 'previous' | 'next') => {
    if (!expandedImage) {
      return
    }

    const room = rooms.find((entry) => entry.id === expandedImage.roomId)
    if (!room || room.images.length === 0) {
      return
    }

    const nextIndex = direction === 'previous'
      ? (expandedImage.imageIndex - 1 + room.images.length) % room.images.length
      : (expandedImage.imageIndex + 1) % room.images.length

    setExpandedImage({
      roomId: room.id,
      roomName: room.name,
      roomType: room.type,
      imageUrl: room.images[nextIndex],
      imageIndex: nextIndex,
      imageCount: room.images.length,
    })
  }

  useEffect(() => {
    if (!expandedImage) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpandedImage(null)
        return
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        stepExpandedImage('previous')
        return
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault()
        stepExpandedImage('next')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [expandedImage, rooms])

  return (
    <div className="min-h-screen bg-gray-50">
      {expandedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 px-4 py-6"
          onClick={() => setExpandedImage(null)}
        >
          <div
            className="relative max-h-full w-full max-w-6xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setExpandedImage(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/70 px-4 py-2 text-sm font-semibold text-white transition hover:bg-black"
            >
              Close
            </button>
            <div className="absolute bottom-3 left-3 z-10 rounded-full bg-black/70 px-4 py-2 text-sm font-medium text-white">
              {expandedImage.roomName}
              {expandedImage.roomType && expandedImage.roomType !== expandedImage.roomName ? ` · ${expandedImage.roomType}` : ''}
              {expandedImage.imageCount > 1 ? ` · ${expandedImage.imageIndex + 1} / ${expandedImage.imageCount}` : ''}
            </div>
            <img
              src={expandedImage.imageUrl}
              alt={`${expandedImage.roomName} full size`}
              className="max-h-[88vh] w-full rounded-2xl object-contain bg-black"
            />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl">Our Units</h1>
          <p className="text-base text-gray-600 sm:text-xl">
            Discover our collection of elegantly designed accommodations
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-600">Loading units…</p>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-16">
            <p className="text-red-600 text-lg">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Filter tabs */}
            {rooms.length > 1 && (
              <div className="mb-8">
                <div className="flex flex-wrap gap-2">
                  {unitTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setSelectedType(type.value)
                        if (type.value === 'All') {
                          void router.push('/booking-system/rooms')
                          return
                        }

                        void router.push(`/booking-system/rooms?type=${encodeURIComponent(type.value)}`)
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedType === type.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Units grid */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
              {filteredRooms.map(room => {
                const activeImageIndex = getActiveImageIndex(room)
                const activeImage = room.images[activeImageIndex]

                return (
                <div key={room.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  {room.images.length > 0 ? (
                    <div className="bg-gray-100 overflow-hidden">
                      <div className="relative h-56 sm:h-48">
                      <img
                        src={activeImage}
                        alt={room.name}
                        className="w-full h-full cursor-zoom-in object-cover"
                        onClick={() => openExpandedImage(room, activeImage, activeImageIndex)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          const parent = target.parentElement
                          if (parent) {
                            parent.className = `h-48 bg-gradient-to-br ${colorMap[room.color] ?? colorMap.blue} flex items-center justify-center`
                            parent.innerHTML = `<div class="text-center text-white"><div class="text-5xl mb-2">🏠</div><div class="text-xl font-bold">${room.name}</div><div class="text-sm opacity-90">Sleeps ${room.capacity} guests</div></div>`
                          }
                        }}
                      />
                      {room.images.length > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={() => setRoomImageIndex(room, activeImageIndex - 1 < 0 ? room.images.length - 1 : activeImageIndex - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 px-2.5 py-2 text-xs font-semibold text-white transition hover:bg-black/70 sm:left-3 sm:px-3 sm:text-sm"
                            aria-label={`View previous image for ${room.name}`}
                          >
                            Prev
                          </button>
                          <button
                            type="button"
                            onClick={() => setRoomImageIndex(room, activeImageIndex + 1 >= room.images.length ? 0 : activeImageIndex + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 px-2.5 py-2 text-xs font-semibold text-white transition hover:bg-black/70 sm:right-3 sm:px-3 sm:text-sm"
                            aria-label={`View next image for ${room.name}`}
                          >
                            Next
                          </button>
                          <div className="absolute bottom-2 right-2 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white sm:bottom-3 sm:right-3 sm:px-3 sm:text-xs">
                            {activeImageIndex + 1} / {room.images.length}
                          </div>
                        </>
                      )}
                      </div>
                      {room.images.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto border-t border-gray-200 bg-white px-3 py-3">
                          {room.images.map((image, index) => (
                            <button
                              key={`${room.id}-${index}`}
                              type="button"
                              onClick={() => setRoomImageIndex(room, index)}
                              className={`h-14 w-16 flex-shrink-0 overflow-hidden rounded-lg border-2 transition sm:h-16 sm:w-20 ${
                                index === activeImageIndex
                                  ? 'border-blue-600 ring-2 ring-blue-200'
                                  : 'border-transparent hover:border-blue-300'
                              }`}
                              aria-label={`View image ${index + 1} for ${room.name}`}
                            >
                              <img
                                src={image}
                                alt={`${room.name} ${index + 1}`}
                                className="h-full w-full object-cover"
                                onDoubleClick={(event) => {
                                  event.stopPropagation()
                                  openExpandedImage(room, image, index)
                                }}
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`h-48 bg-gradient-to-br ${colorMap[room.color] ?? colorMap.blue} flex items-center justify-center`}>
                      <div className="text-center text-white">
                        <div className="text-5xl mb-2">🏠</div>
                        <div className="text-xl font-bold">{room.name}</div>
                        {room.type !== room.name && (
                          <div className="text-sm opacity-90">{room.type}</div>
                        )}
                        <div className="text-sm opacity-90">Sleeps {room.capacity} guests</div>
                      </div>
                    </div>
                  )}

                  <div className="p-4 sm:p-6">
                    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 sm:text-xl">{room.name}</h3>
                        {room.type !== room.name && (
                          <p className="text-sm font-medium text-gray-500 mt-1">{room.type}</p>
                        )}
                        {room.description && (
                          <p className="text-gray-600 text-sm mt-1">{room.description}</p>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        room.available
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {room.available ? 'Available' : 'Fully Booked'}
                      </span>
                    </div>

                    {/* Amenities */}
                    {room.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {room.amenities.slice(0, 5).map((a, i) => (
                          <span key={i} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">{a}</span>
                        ))}
                        {room.amenities.length > 5 && (
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                            +{room.amenities.length - 5} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Pricing */}
                    <div className="mb-4 grid grid-cols-1 gap-2 text-center text-xs sm:grid-cols-3">
                      <div className="rounded bg-green-50 px-2 py-2 text-green-800">
                        <div className="font-semibold">Low Season</div>
                        <div>R{room.pricing.low_season.toLocaleString()}/night</div>
                      </div>
                      <div className="rounded bg-yellow-50 px-2 py-2 text-yellow-800">
                        <div className="font-semibold">Mid Season</div>
                        <div>R{room.pricing.mid_season.toLocaleString()}/night</div>
                      </div>
                      <div className="rounded bg-red-50 px-2 py-2 text-red-800">
                        <div className="font-semibold">High Season</div>
                        <div>R{room.pricing.high_season.toLocaleString()}/night</div>
                      </div>
                    </div>

                    {room.pricing.min_stay > 1 && (
                      <p className="text-xs text-gray-500 mb-4">Minimum stay: {room.pricing.min_stay} nights</p>
                    )}

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <span className="text-2xl font-bold text-blue-600">
                          R{room.pricing.low_season.toLocaleString()}
                        </span>
                        <span className="text-gray-500 text-sm">/night from</span>
                      </div>
                      <a
                        href={room.available ? `/booking-system/booking?room=${room.id}` : undefined}
                        aria-disabled={!room.available}
                        onClick={e => { if (!room.available) e.preventDefault() }}
                        className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                          room.available
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {room.available ? 'Book Now' : 'Unavailable'}
                      </a>
                    </div>
                  </div>
                </div>
              )})}
            </div>

            {filteredRooms.length === 0 && (
              <div className="text-center py-16">
                <div className="text-5xl mb-4">🏠</div>
                <p className="text-xl text-gray-600">
                  {rooms.length === 0
                    ? 'No units are currently available. Please check back soon.'
                    : 'No units found for the selected category.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default RoomsPage